import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { ProductService } from '../services/ProductService';
import MessageService from '../services/MessageService';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  ImageList,
  ImageListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Warning,
  ShoppingCart,
  Favorite,
  FavoriteBorder,
  Share,
  Compare,
  LocalShipping,
  Security,
  Assignment,
  NavigateNext,
  Message,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { CartService } from '../services/CartService';
import { WishlistService } from '../services/WishlistService';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import { RatingComponent } from '../components/Rating/RatingComponent';
import { RatingService } from '../services/RatingService';

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const [selectedImage, setSelectedImage] = useState(0);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: product, isLoading, error } = useQuery(['product', id], () =>
    ProductService.getProductById(id)
  );

  const { data: seller } = useQuery(
    ['seller', product?.sellerId],
    () => ProductService.getSellerInfo(product.sellerId),
    {
      enabled: !!product?.sellerId,
    }
  );

  const { data: averageRating } = useQuery(
    ['productRating', id],
    () => RatingService.getProductAverageRating(id),
    {
      enabled: !!id
    }
  );

  const { data: ratings } = useQuery(
    ['productRatings', id],
    () => RatingService.getProductRatings(id),
    {
      enabled: !!id
    }
  );

  const sendMessageMutation = useMutation(
    (message) => MessageService.sendMessage({
      senderId: currentUser.uid,
      receiverId: product.sellerId,
      message: message,
      productId: product.id
    }),
    {
      onSuccess: () => {
        enqueueSnackbar(t('product.messageSent'), { variant: 'success' });
        setMessage('');
        navigate('/profile', { 
          state: { tabValue: 1 } // Messages tab
        });
      },
      onError: (error) => {
        console.error('Error sending message:', error);
        enqueueSnackbar(error.message || t('product.messageError'), { 
          variant: 'error' 
        });
      }
    }
  );

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    try {
      await sendMessageMutation.mutateAsync(message.trim());
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addToCartMutation = useMutation(
    () => CartService.addToCart(product?.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cart');
        enqueueSnackbar(t('product.cart.addedToCart'), { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(t('product.cart.failedToAdd'), { variant: 'error' });
      }
    }
  );

  const { data: isInWishlist } = useQuery(
    ['wishlist', product?.id],
    () => WishlistService.isInWishlist(product?.id),
    {
      enabled: !!currentUser && !!product?.id
    }
  );

  const toggleWishlistMutation = useMutation(
    () => isInWishlist ? 
      WishlistService.removeFromWishlist(product?.id) : 
      WishlistService.addToWishlist(product?.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['wishlist', product?.id]);
        queryClient.invalidateQueries('wishlist');
        enqueueSnackbar(
          isInWishlist 
            ? t('product.wishlist.removedFromWishlist') 
            : t('product.wishlist.addedToWishlist'),
          { variant: 'success' }
        );
      }
    }
  );

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: product?.title,
          text: product?.description,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        enqueueSnackbar('Link copied to clipboard', { variant: 'success' });
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        enqueueSnackbar('Failed to share', { variant: 'error' });
      }
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load product details. Please try again later.
      </Alert>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('breadcrumbs.home')}
        </Link>
        <Link
          component={RouterLink}
          to="/search"
          color="inherit"
        >
          {t('breadcrumbs.parts')}
        </Link>
        {product?.category && (
          <Link
            component={RouterLink}
            to={`/search?category=${product.category}`}
            color="inherit"
          >
            {t(`breadcrumbs.categories.${product.category.toLowerCase()}`)}
          </Link>
        )}
        <Typography color="text.primary">
          {product?.title}
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Images Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 400,
                mb: 2,
                borderRadius: 1,
                overflow: 'hidden'
              }}
            >
              <img
                src={product.images[selectedImage]}
                alt={product.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
            <ImageList cols={4} rowHeight={100} gap={8}>
              {product.images.map((image, index) => (
                <ImageListItem 
                  key={index}
                  sx={{
                    cursor: 'pointer',
                    border: index === selectedImage ? `2px solid ${theme.palette.primary.main}` : 'none',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                  onClick={() => setSelectedImage(index)}
                >
                  <img
                    src={image}
                    alt={`${product.title} ${index + 1}`}
                    loading="lazy"
                    style={{ objectFit: 'cover', height: '100%' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Paper>
        </Grid>

        {/* Details Section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" gutterBottom fontWeight="bold">
                {product.title}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip 
                  label={t(`product.conditions.${product.condition.toLowerCase()}`)} 
                  color="primary" 
                />
                <Chip 
                  label={t(`product.categories.${product.category.toLowerCase()}`)} 
                  variant="outlined" 
                />
                <Chip 
                  label={t('product.stockCount', { count: product.stock })}
                  color={product.stock > 0 ? "success" : "error"}
                />
              </Box>

              <Typography variant="h4" color="primary" gutterBottom fontWeight="bold">
                ${product.price.toFixed(2)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={averageRating || 0} precision={0.5} readOnly />
                <Typography variant="body2" color="text.secondary">
                  {t('rating.outOf')} 5 ({ratings?.ratings?.length || 0} {t('rating.reviews')})
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" paragraph>
              {product.description}
            </Typography>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('product.specifications.attribute')}</TableCell>
                    <TableCell>{t('product.specifications.value')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{t('product.brand')}</TableCell>
                    <TableCell>{product?.brand || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('product.model')}</TableCell>
                    <TableCell>{product?.model || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('product.year.label')}</TableCell>
                    <TableCell>{product?.year || '-'}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('product.condition')}</TableCell>
                    <TableCell>
                      {product?.condition 
                        ? t(`product.conditions.${product.condition.toLowerCase()}`)
                        : '-'
                      }
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{t('product.category')}</TableCell>
                    <TableCell>
                      {product?.category 
                        ? t(`product.categories.${product.category.toLowerCase()}`)
                        : '-'
                      }
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Tooltip title={product?.stock <= 0 ? t('product.outOfStock') : t('product.addToCart')}>
                <span> {/* Wrapper to maintain tooltip when button is disabled */}
                  <IconButton
                    onClick={() => addToCartMutation.mutate()}
                    disabled={!currentUser || product?.stock <= 0}
                    color="primary"
                    sx={{
                      bgcolor: 'action.hover',
                      '&:hover': {
                        bgcolor: 'primary.light',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'action.disabledBackground',
                      }
                    }}
                  >
                    <ShoppingCart />
                  </IconButton>
                </span>
              </Tooltip>

              {currentUser && (
                <Tooltip title={isInWishlist ? t('product.removeFromWishlist') : t('product.addToWishlist')}>
                  <IconButton
                    onClick={() => toggleWishlistMutation.mutate()}
                    color={isInWishlist ? "primary" : "default"}
                    sx={{
                      bgcolor: 'action.hover',
                      '&:hover': {
                        bgcolor: isInWishlist ? 'primary.light' : 'action.selected'
                      }
                    }}
                  >
                    {isInWishlist ? <Favorite /> : <FavoriteBorder />}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t('product.share')}>
                <IconButton
                  onClick={handleShare}
                  sx={{
                    bgcolor: 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Seller Info Section with proper spacing */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {t('product.sellerInfo')}
            </Typography>
            <Paper sx={{ p: 3, borderRadius: 2, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar 
                  src={seller?.photoURL} 
                  sx={{ width: 64, height: 64 }}
                >
                  {seller?.name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {seller?.name || t('common.anonymous')}
                  </Typography>
                  {seller?.createdAt && (
                    <Typography variant="body2" color="text.secondary">
                      {t('product.memberSince')} {' '}
                      {seller.createdAt instanceof Date 
                        ? seller.createdAt.toLocaleDateString()
                        : seller.createdAt?.toDate?.()?.toLocaleDateString() || 
                          new Date(seller.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Contact Options - Remove seller check from condition */}
              {seller && currentUser && (
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  {/* Message Button - Hide only if current user is seller */}
                  {(
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        if (!currentUser) {
                          enqueueSnackbar(t('product.pleaseLogin'), { variant: 'warning' });
                          navigate('/auth');
                          return;
                        }
                        setMessageDialogOpen(true);
                      }}
                      startIcon={<EmailIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'primary.main'
                        }
                      }}
                    >
                      {t('product.contactSeller')}
                    </Button>
                  )}

                  {/* Phone Button - Always show */}
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => {
                      if (!currentUser) {
                        enqueueSnackbar(t('product.pleaseLogin'), { variant: 'warning' });
                        navigate('/auth');
                        return;
                      }
                      navigator.clipboard.writeText(seller?.phone);
                      enqueueSnackbar(t('product.phoneNumberCopied'), { variant: 'success' });
                    }}
                    startIcon={<PhoneIcon />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: 'success.light',
                        color: 'success.main'
                      }
                    }}
                  >
                    {seller?.phone || t('product.phoneNotAvailable')}
                  </Button>
                </Box>
              )}
            </Paper>

            
          </Paper>
        </Grid>
      </Grid>

      {/* Message Dialog */}
      <Dialog
        open={messageDialogOpen}
        onClose={() => {
          setMessageDialogOpen(false);
          setMessage('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle>{t('product.message.contactSeller')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('product.message.writeMessage')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('product.message.placeholder')}
              variant="outlined"
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={() => {
              setMessageDialogOpen(false);
              setMessage('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={sendMessageMutation.isLoading || !message.trim()}
          >
            {sendMessageMutation.isLoading ? t('product.message.sending') : t('product.message.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 4 }}>
        <RatingComponent 
          productId={product.id} 
          sellerId={product.sellerId}
          onRatingAdded={() => {
            // Refresh product data using queryClient
            queryClient.invalidateQueries(['product', id]);
          }}
        />
      </Box>
    </Container>
  );
} 